import { signal, SignalValue } from "omi";

import { NetworkSignal, networkSignal } from "@/utils/signals";
import { Day, GetUserScheduleRequest, ProjectStatus, Schedule, Task } from "@/api";
import { DaysPerWeek, HoursPerDay } from "@/models";
import { Store as GlobalStore } from "@/store";
import { initApi } from "@/utils";

const api = initApi();
const schedule = networkSignal<Schedule>();
const tasks = signal<Task[]>([]);
const currentDay = signal<Day | undefined>(undefined);

export type UseScheduleStore = ReturnType<typeof useSchedule>;
export function useSchedule() {
  const fetch = async (
    opts: { hourPerDay?: HoursPerDay; daysPerWeek?: DaysPerWeek; topics?: string[] } | undefined,
  ) => {
    const res = await schedule.run(() => getSchedule(opts?.hourPerDay, opts?.daysPerWeek, opts?.topics));
    if (res != null) {
      tasks.value = res.weeks.flatMap(week => week.days.flatMap(day => day.tasks));
      currentDay.value = getCurrentDaySelector(res)();
    }
  };

  return {
    ...schedule.signal,
    fetch,
    updateProjectStatus: updateProjectStatusFunc(schedule),
    getNextTask: getNextTaskSelector(schedule, tasks.value, GlobalStore.project, GlobalStore.user),
    hasSchedule: hasScheduleSelector(GlobalStore.user),
    getDaysTasks: getDaysTasksSelector(currentDay),
  };
}

async function getSchedule(hoursPerDay?: HoursPerDay, daysPerWeek?: DaysPerWeek, topics?: string[]): Promise<Schedule> {
  const payload: Partial<GetUserScheduleRequest> = {
    hoursPerDay,
    daysPerWeek,
  };

  if (topics != null) {
    payload.topics = topics;
  }

  return await api.getUserSchedule(payload);
}

function hasScheduleSelector(userStore: GlobalStore["user"]): () => boolean {
  return () =>
    userStore.preferences.daysPerWeek.value !== DaysPerWeek.NONE &&
    userStore.preferences.hoursPerDay.value !== HoursPerDay.NONE;
}

function getDaysTasksSelector(currentDay: SignalValue<Day | undefined>): () => Task[] {
  return () => {
    return currentDay.value?.tasks || [];
  };
}

function getCurrentDaySelector(schedule: Schedule): () => Day | undefined {
  return () => {
    const currentProjectId = GlobalStore.project.id.value;
    return schedule.weeks.flatMap(week => week.days).find(day => day.tasks.some(task => task.id === currentProjectId));
  };
}

function updateProjectStatusFunc(store: NetworkSignal<Schedule>): (projectId: string, status: ProjectStatus) => void {
  return (projectId, status) => {
    if (store.signal.data.value == null) {
      return;
    }

    const weeks = [...store.signal.data.value.weeks];

    for (const week of weeks) {
      let wasDayUpdated = false;

      for (const day of week.days) {
        let wasUpdated = false;
        for (const task of day.tasks) {
          if (task.id === projectId) {
            task.status = status;
            wasUpdated = true;
            wasDayUpdated = true;
          }
        }

        if (wasUpdated) {
          day.status = day.tasks.every(task => task.status === ProjectStatus.PROJECT_COMPLETE)
            ? ProjectStatus.PROJECT_COMPLETE
            : ProjectStatus.PROJECT_INCOMPLETE;
          wasUpdated = false;
        }
      }

      if (wasDayUpdated) {
        week.status = week.days.every(task => task.status === ProjectStatus.PROJECT_COMPLETE)
          ? ProjectStatus.PROJECT_COMPLETE
          : ProjectStatus.PROJECT_INCOMPLETE;
        wasDayUpdated = false;
      }
    }

    store.signal.data.value = {
      ...store.signal.data.value,
      weeks: weeks,
    };
  };
}

export function getNextTaskSelector(
  store: NetworkSignal<Schedule>,
  allTasks: Task[],
  projectStore: GlobalStore["project"],
  userStore: GlobalStore["user"],
): () => Task | undefined {
  const hasSchedule = hasScheduleSelector(userStore);
  return () => {
    if (store.signal.data.value == null || !hasSchedule()) {
      return undefined;
    }
    let dayFound = false;
    for (let i = 0; i < allTasks.length; i++) {
      const task = allTasks[i];

      if (dayFound && task.status === ProjectStatus.PROJECT_INCOMPLETE) {
        return task;
      }

      if (task.id === projectStore.id.value && i === allTasks.length - 1) {
        return allTasks.find(task => task.status === ProjectStatus.PROJECT_INCOMPLETE);
      }

      if (task.id === projectStore.id.value) {
        dayFound = true;
      }
    }

    return allTasks.find(task => task.status === ProjectStatus.PROJECT_INCOMPLETE);
  };
}
